import type { RoastProfileRow, RoastStageState } from '../../roasts/component/types';
import { calculateRorForRows } from '../../roasts/component/RoastProfileUtils';

// ─── Artisan JSON shape (subset we care about) ─────────────────────

export interface ArtisanJson {
    /** Stable per-roast identifier Artisan writes into every export. Used for duplicate detection. */
    roastUUID?: string;
    title?: string;
    beans?: string;
    roastertype?: string;
    operator?: string;
    organization?: string;
    roastisodate?: string;
    roasttime?: string;
    drumspeed?: string;
    weight?: [number, number, string];
    whole_color?: number;
    ground_color?: number;

    timex: number[];
    temp1: number[]; // ET (Environment / Exhaust)
    temp2: number[]; // BT (Bean Temp)

    timeindex: number[]; // [CHARGE_idx, DRY_idx, FCs_idx, FCe_idx, SCs_idx, SCe_idx, DROP_idx, COOL_idx]

    specialevents?: number[];
    specialeventstype?: number[];
    specialeventsvalue?: number[];
    etypes?: string[];

    computed?: {
        CHARGE_BT?: number;
        CHARGE_ET?: number;
        TP_time?: number;
        TP_BT?: number;
        DRY_time?: number;
        DRY_BT?: number;
        FCs_time?: number;
        FCs_BT?: number;
        DROP_time?: number;
        DROP_BT?: number;
        totaltime?: number;
        dryphasetime?: number;
        midphasetime?: number;
        finishphasetime?: number;
    };
}

// ─── Helpers ────────────────────────────────────────────────────────

const secondsToTimeStr = (totalSeconds: number): string => {
    const m = Math.floor(totalSeconds / 60);
    const s = Math.floor(totalSeconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Find the value from the `timex` array at a given index,
 * offset by the CHARGE time so time starts at 00:00 relative to charge.
 */
const relativeSeconds = (timex: number[], chargeIdx: number, idx: number): number => {
    const chargeTime = timex[chargeIdx] ?? 0;
    return Math.round((timex[idx] ?? 0) - chargeTime);
};

/**
 * Interpolate a temperature at a given absolute time (in seconds since epoch start)
 * from the timex / temp arrays.
 */
const interpolateTemp = (timex: number[], temps: number[], targetTime: number): number | null => {
    if (timex.length === 0 || temps.length === 0) return null;

    // Exact match or boundary
    for (let i = 0; i < timex.length; i++) {
        if (Math.abs(timex[i] - targetTime) < 0.5) return Math.round(temps[i] * 10) / 10;
    }

    // Interpolation
    for (let i = 0; i < timex.length - 1; i++) {
        if (timex[i] <= targetTime && timex[i + 1] >= targetTime) {
            const ratio = (targetTime - timex[i]) / (timex[i + 1] - timex[i]);
            const val = temps[i] + ratio * (temps[i + 1] - temps[i]);
            return Math.round(val * 10) / 10;
        }
    }

    return null;
};

/**
 * Artisan stores event values internally as `external / 10 + 1`
 * (see Artisan's `eventsInternal2ExternalValue`), so the real slider
 * reading is `(raw - 1) * 10`. e.g. 2.5 -> 15, 3.5 -> 25, 2.2 -> 12.
 * A raw value of 0 is Artisan's "event carries no value" sentinel.
 */
const decodeEventValue = (raw: number): number | null => {
    if (!Number.isFinite(raw) || raw <= 0) return null;
    return Math.max(0, Math.round((raw - 1) * 10));
};

/**
 * `etypes` labels are user-configurable and their ORDER is not fixed:
 * Artisan ships with ['Air', 'Drum', 'Damper', 'Burner', '--'] but a machine
 * setup may rename/reorder them (e.g. ['Speed', 'Power', 'Damper', 'Fan', '--']),
 * so the slot must be resolved by label, never by position.
 * Each group is tried exact-first then substring, in priority order.
 */
const FIRE_LABEL_GROUPS = [['power', 'burner', 'fire', 'flame', 'gas', 'heat', 'heater']];
const AIRFLOW_LABEL_GROUPS = [['air', 'airflow', 'fan', 'blower'], ['damper']];

// Positional fallback: Artisan's factory defaults, used only when no label matches.
const DEFAULT_FIRE_TYPE_IDX = 3; // 'Burner'
const DEFAULT_AIRFLOW_TYPE_IDX = 0; // 'Air'

const findEventTypeIdx = (etypes: string[], groups: string[][], fallbackIdx: number): number => {
    const labels = etypes.map((l) => (l ?? '').trim().toLowerCase());

    for (const group of groups) {
        for (const keyword of group) {
            const exact = labels.indexOf(keyword);
            if (exact !== -1) return exact;
        }
        for (const keyword of group) {
            const partial = labels.findIndex((l) => l.includes(keyword));
            if (partial !== -1) return partial;
        }
    }

    return fallbackIdx < etypes.length ? fallbackIdx : -1;
};

/**
 * Artisan logs an event the moment the operator moves the slider, which is often
 * a beat after CHARGE — the setting itself was already live when the beans went in
 * (in DERS-290726A the charge settings land at 00:04). When a channel's first
 * event falls inside the opening sampling interval, carry it back to 00:00 so the
 * first row isn't blank. Beyond that window the pre-event value is genuinely
 * unknown and is left empty rather than invented.
 */
const EVENT_BACKFILL_WINDOW_SECONDS = 30;

const backfillFirstEvent = (events: Map<number, number>): void => {
    if (events.size === 0 || events.has(0)) return;

    const firstSec = Math.min(...events.keys());
    if (firstSec > EVENT_BACKFILL_WINDOW_SECONDS) return;

    events.set(0, events.get(firstSec)!);
};

/**
 * Build fire/airflow lookup maps from specialevents.
 * Artisan stores events as indices into `timex`; `specialeventstype[i]` is the
 * index into `etypes`, and event times are relative to CHARGE (which is rarely
 * index 0 — the recording usually starts before the beans are dropped in).
 */
const buildEventMap = (
    data: ArtisanJson,
    chargeIdx: number,
    dropIdx: number,
): { fireEvents: Map<number, number>; airflowEvents: Map<number, number> } => {
    const fireEvents = new Map<number, number>();
    const airflowEvents = new Map<number, number>();

    if (!data.specialevents || !data.specialeventstype || !data.specialeventsvalue || !data.etypes) {
        return { fireEvents, airflowEvents };
    }

    const powerTypeIdx = findEventTypeIdx(data.etypes, FIRE_LABEL_GROUPS, DEFAULT_FIRE_TYPE_IDX);
    const airflowTypeIdx = findEventTypeIdx(data.etypes, AIRFLOW_LABEL_GROUPS, DEFAULT_AIRFLOW_TYPE_IDX);
    if (powerTypeIdx === -1 && airflowTypeIdx === -1) {
        return { fireEvents, airflowEvents };
    }

    // A roast without a DROP marker still has usable events — fall back to the tail.
    const lastIdx = dropIdx > chargeIdx ? dropIdx : data.timex.length - 1;

    // Apply in chronological order so a later event wins when several collapse
    // onto the same 0s slot after clamping.
    const order = data.specialevents
        .map((_, i) => i)
        .sort((a, b) => data.specialevents![a] - data.specialevents![b]);

    for (const i of order) {
        const eventIdx = data.specialevents[i];
        const eventType = data.specialeventstype[i];
        const value = decodeEventValue(data.specialeventsvalue[i]);

        if (value === null || eventIdx < 0 || eventIdx >= data.timex.length || eventIdx > lastIdx) continue;

        // Settings dialled in before CHARGE are the settings the roast starts with.
        const relSec = Math.max(0, relativeSeconds(data.timex, chargeIdx, eventIdx));

        if (eventType === powerTypeIdx) {
            fireEvents.set(relSec, value);
        } else if (eventType === airflowTypeIdx) {
            airflowEvents.set(relSec, value);
        }
    }

    backfillFirstEvent(fireEvents);
    backfillFirstEvent(airflowEvents);

    return { fireEvents, airflowEvents };
};

// ─── Main parser ────────────────────────────────────────────────────

export interface ParsedArtisanData {
    profile: RoastProfileRow[];
    stages: RoastStageState[];
    weightInput: number;
    weightOutput: number;
    agtronWhole: number;
    agtronGround: number;
    roastType: string;
    metadata: {
        title: string;
        beans: string;
        machine: string;
        operator: string;
        organization: string;
        roastDate: string;
        roastTime: string;
        drumSpeed: string;
        weightIn: number;
        weightOut: number;
        weightUnit: string;
        wholeColor: number;
        groundColor: number;
    };
}

export function parseArtisanJson(data: ArtisanJson): ParsedArtisanData {
    const { timex, temp1, temp2, timeindex, computed } = data;

    // timeindex: [CHARGE, DRY, FCs, FCe, SCs, SCe, DROP, COOL]
    const chargeIdx = timeindex[0] ?? 0;
    const dryIdx = timeindex[1] ?? 0;
    const fcsIdx = timeindex[2] ?? 0;
    const dropIdx = timeindex[6] ?? 0;

    const chargeTime = timex[chargeIdx] ?? 0;
    const dropTime = timex[dropIdx] ?? timex[timex.length - 1] ?? 0;
    const totalRoastSeconds = Math.round(dropTime - chargeTime);

    // ── Build event maps ──
    const { fireEvents, airflowEvents } = buildEventMap(data, chargeIdx, dropIdx);

    // ── Resample to 30-second intervals ──
    const rowCount = Math.floor(totalRoastSeconds / 30) + 1;
    const rows: RoastProfileRow[] = [];

    let lastFire: number | null = null;
    let lastAirflow: number | null = null;

    // Pre-sort fire/airflow events by time
    const sortedFireKeys = [...fireEvents.keys()].sort((a, b) => a - b);
    const sortedAirflowKeys = [...airflowEvents.keys()].sort((a, b) => a - b);

    for (let i = 0; i <= rowCount; i++) {
        const relSec = i * 30;
        if (relSec > totalRoastSeconds + 15) break; // Don't go too far past drop

        const absTime = chargeTime + relSec;

        const beanTemp = interpolateTemp(timex, temp2, absTime);
        const environmentTemp = interpolateTemp(timex, temp1, absTime);

        // Forward-fill fire: find the latest fire event at or before this time
        for (const k of sortedFireKeys) {
            if (k <= relSec) lastFire = fireEvents.get(k) ?? lastFire;
            else break;
        }

        // Forward-fill airflow: find the latest airflow event at or before this time
        for (const k of sortedAirflowKeys) {
            if (k <= relSec) lastAirflow = airflowEvents.get(k) ?? lastAirflow;
            else break;
        }

        rows.push({
            time: secondsToTimeStr(relSec),
            seconds: relSec,
            bean: beanTemp,
            ror: null, // Will be calculated below
            fire: lastFire,
            airflow: lastAirflow,
            environmentTemp: environmentTemp,
        });
    }

    const profileWithRor = calculateRorForRows(rows);

    // ── Build stages ──
    const buildTime = (idx: number): string => {
        if (idx <= 0 || idx >= timex.length) return '';
        return secondsToTimeStr(relativeSeconds(timex, chargeIdx, idx));
    };

    // For turning point, we rely on computed.TP_time (relative to CHARGE in seconds)
    const tpTime = computed?.TP_time != null ? secondsToTimeStr(computed.TP_time) : '';

    const stages: RoastStageState[] = [
        {
            key: 'start',
            label: 'Start',
            time: '00:00',
            temp: computed?.CHARGE_BT ?? Math.round(temp2[chargeIdx] ?? 0),
        },
        {
            key: 'turning_point',
            label: 'Turning Point',
            time: tpTime,
            temp: computed?.TP_BT ?? '',
        },
        {
            key: 'yellow',
            label: 'Yellow',
            time: dryIdx > 0 ? buildTime(dryIdx) : (computed?.DRY_time != null ? secondsToTimeStr(computed.DRY_time) : ''),
            temp: computed?.DRY_BT ?? '',
        },
        {
            key: 'first_crack',
            label: 'First Crack',
            time: fcsIdx > 0 ? buildTime(fcsIdx) : (computed?.FCs_time != null ? secondsToTimeStr(computed.FCs_time) : ''),
            temp: computed?.FCs_BT ?? '',
        },
        {
            key: 'end',
            label: 'End',
            time: dropIdx > 0 ? buildTime(dropIdx) : (computed?.DROP_time != null ? secondsToTimeStr(computed.DROP_time) : ''),
            temp: computed?.DROP_BT ?? '',
        },
    ];

    // ── Metadata ──
    const metadata = {
        title: data.title || '',
        beans: data.beans || '',
        machine: data.roastertype || '',
        operator: data.operator || '',
        organization: data.organization || '',
        roastDate: data.roastisodate || '',
        roastTime: data.roasttime || '',
        drumSpeed: data.drumspeed || '',
        weightIn: data.weight?.[0] ?? 0,
        weightOut: data.weight?.[1] ?? 0,
        weightUnit: data.weight?.[2] ?? 'Kg',
        wholeColor: data.whole_color ?? 0,
        groundColor: data.ground_color ?? 0,
    };

    return {
        profile: profileWithRor,
        stages,
        metadata,
        weightInput: metadata.weightIn,
        weightOutput: metadata.weightOut,
        agtronWhole: metadata.wholeColor,
        agtronGround: metadata.groundColor,
        // An Artisan .alog export carries no roast-type taxonomy, so there is nothing
        // here to map onto sample/filter/omni/espresso. Guessing wrote values the Roast
        // Type selection does not contain, which then surfaced in the WO list (WO-028).
        // The work order owns its roast type; the profile leaves it unset.
        roastType: '',
    };
}
